/**
 * User Management service — non-CRUD endpoints. Only `getList` flows
 * through the dataProvider; all writes (add / update / delete /
 * set-password) hit the dedicated platform-user routes Angular calls.
 */

import { httpClient } from '@/utils/httpClient';
import type { NotifyEnvelope } from '@/types/api';
import type { UserRecord, UserUpdatePayload } from '../types/user';

/**
 * `PUT /update-user/{id}` — updates name/surname/email of an (account)
 * user and syncs the change to Keycloak. Never touches platform roles.
 */
export const updateUser = async (
  id: string,
  body: UserUpdatePayload
): Promise<void> => {
  await httpClient.put(`/update-user/${encodeURIComponent(id)}`, body);
};

/** `GET /user/{id}` — Angular's `getUserById`. */
export const getUserById = async (id: string): Promise<UserRecord> => {
  const { data } = await httpClient.get<NotifyEnvelope<UserRecord>>(
    `/user/${encodeURIComponent(id)}`
  );
  return data.Response;
};

/**
 * `GET /user/{id}/notify-staff` — si esta persona es personal del contact center de
 * Roommatik. Se pregunta a Keycloak (ahi vive el rol `app-notify-staff`), no a la ficha
 * local: los permisos de la ficha los reparte el hotel desde su portal, y este no puede ser
 * uno de ellos —seria un permiso que el hotel podria concederse a si mismo—.
 *
 * Solo SuperUser. Es lo unico que abre el diario interno del contact center.
 */
export const getNotifyStaff = async (id: string): Promise<boolean> => {
  const { data } = await httpClient.get<NotifyEnvelope<{ enabled: boolean }>>(
    `/user/${encodeURIComponent(id)}/notify-staff`
  );
  return data.Response?.enabled === true;
};

/** `PUT /user/{id}/notify-staff` — concede o retira ese rol. Idempotente. */
export const setNotifyStaff = async (id: string, enabled: boolean): Promise<void> => {
  await httpClient.put(`/user/${encodeURIComponent(id)}/notify-staff`, { enabled });
};

/** `DELETE /user/{id}` — Angular's `deleteUser`. */
export const deleteUser = async (id: string): Promise<void> => {
  await httpClient.delete(`/user/${encodeURIComponent(id)}`);
};

/**
 * `PUT /user/set-user-password` — Angular's `setUserPassword`. Body:
 * email + password (confirm-password is form-only and stripped).
 */
export const setUserPassword = async (
  email: string,
  password: string
): Promise<void> => {
  await httpClient.put('/user/set-user-password', { email, password });
};

/** Block / unblock (local row + Keycloak Enabled). */
export const setUserEnabled = async (id: string, enabled: boolean): Promise<void> => {
  await httpClient.put(`/user/${encodeURIComponent(id)}/enabled`, { enabled });
};

/** Definitive delete: Keycloak identity + local row. Not reversible. */
export const deleteUserHard = async (id: string): Promise<void> => {
  await httpClient.delete(`/user/${encodeURIComponent(id)}/hard`);
};

export interface UserAccountRef {
  id: string;
  accountName: string;
  /** Permisos del usuario EN esa cuenta, ya resueltos y recortados a lo contratado. */
  scopes?: string[];
  /** false = nadie se los ha configurado ahi; vienen del sistema antiguo. */
  scopesConfigured?: boolean;
}

/** Accounts the user belongs to (live from Keycloak groups). */
export const getUserAccounts = async (id: string): Promise<UserAccountRef[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<UserAccountRef[]>>(
    `/user/${encodeURIComponent(id)}/accounts`,
    { skipGlobalLoader: true },
  );
  return data.Response ?? [];
};

/** Replaces the user's account memberships (diff-based server-side). */
export const setUserAccounts = async (id: string, accountIds: string[]): Promise<void> => {
  await httpClient.put(`/user/${encodeURIComponent(id)}/accounts`, { accountIds });
};
